import React from "react";
import {
  Autocomplete,
  TextField,
  TextFieldProps,
  SxProps,
  Grid,
} from "@mui/material";
import {
  Control,
  Controller,
  ControllerProps,
  useFormContext,
} from "react-hook-form";
import CustomTooltip from "./customTooltip";

export type AutoCompleteSelectProps = Omit<
  TextFieldProps,
  "name" | "type" | "onChange"
> & {
  validation?: ControllerProps["rules"];
  name: string;
  toolTipTitle?: string;
  options?: any[];
  valueKey?: string;
  labelKey?: string;
  type?: "string" | "number";
  sxProps?: SxProps;
  objectOnChange?: boolean;
  onChange?: (value: any) => void;
  control?: Control<any>;
  fieldName: string;
  label?: string;
};

export default function CustomAutoCompleteSelect({
  name,
  toolTipTitle,
  required,
  fieldName,
  valueKey = "id",
  labelKey = "title",
  options = [],
  sxProps,
  objectOnChange,
  validation = {},
  control,
  label,
  onChange,
  ...rest
}: AutoCompleteSelectProps): JSX.Element {
  const { setValue, getValues } = useFormContext();

  return (
    <Controller
      name={name}
      rules={validation}
      control={control}
      render={({
        field: { onBlur, onChange: fieldOnChange, value },
        fieldState: { error },
      }) => {
        return (
          <Autocomplete
            options={options}
            size="small"
            autoHighlight
            value={value || null}
            defaultValue={null}
            sx={{
              "& .MuiAutocomplete-option": {
                "&.Mui-selected": {
                  backgroundColor: "#cccccc !important",
                },
              },
            }}
            onChange={(event, item) => {
              fieldOnChange(item);
              if (item) {
                setValue(fieldName, item[valueKey]);
                if (typeof onChange === "function") {
                  onChange(item);
                }
              }
            }}
            getOptionLabel={(option) => (option ? option[labelKey] : "")}
            isOptionEqualToValue={(option, value) =>
              option && value
                ? option[valueKey] === value[valueKey]
                : option === value
            }
            renderOption={(props, option, { selected }) => (
              <li
                {...props}
                style={{
                  backgroundColor: selected ? "#cccccc !important" : "#fff !important",
                  color: selected ? "#000" : "#797979",
                  fontFamily: "Poppins",
                  fontWeight: "300",
                  fontSize: "16px",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#ccc !important";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = selected ? "#cccccc !important" : "#fff !important";
                }}
              >
                {option[labelKey]}
              </li>
            )}
            componentsProps={{
              popper: {
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 5],
                    },
                  },
                ],
                sx: {
                  "& .MuiAutocomplete-paper": {
                    backgroundColor: "#fff",
                    color: "#797979",
                  },
                },
              },
            }}
            renderInput={(params) => (
              <Grid container alignItems="center">
                <Grid item>
                  <Grid container alignItems="center">
                    <Grid item>
                      <Grid container alignItems="center">
                        <Grid
                          item
                          sx={{
                            color: "#A5A5A5",
                            fontSize: "14px",
                            fontFamily: "Poppins",
                            fontWeight: "300",
                            mb: "4px",
                            ml: "4px",
                            mr: "6px",
                          }}
                        >
                          {label}
                        </Grid>
                        {toolTipTitle && <CustomTooltip title={toolTipTitle} />}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <TextField
                  {...params}
                  name={name}
                  variant="outlined"
                  fullWidth
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "new-password",
                  }}
                  error={!!error}
                  helperText={error ? error.message : rest?.helperText}
                  required={required}
                  sx={{
                    bgcolor: "transparent",
                    borderRadius: "100px",
                    color: "#797979",
                    fontWeight: "300",
                    "& .MuiInputLabel-root": {
                      color: "#ffffff", // Label color
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "100px",
                      color: "#797979",
                      fontSize: "16px",
                      fontWeight: "200",
                      backgroundColor: "#fff",
                      "& fieldset": {
                        borderRadius: "100px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ccc", // Hovered border color
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ccc", // Focused border color
                      },
                      "& input": {
                        color: "#000", // Text color inside input
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#A5A5A5", // Dropdown icon color
                      },
                      "& .MuiSelect-select": {
                        color: "#000", // Selected value color
                      },
                    },
                    "& .MuiPaper-root": {
                      backgroundColor: "#fff !important", // Dropdown options list background color
                      color: "#000", // Text color for the options
                      "& .MuiMenuItem-root:hover": {
                        backgroundColor: "#fff",
                        color: "#000",
                      },
                    },
                  }}
                />
              </Grid>
            )}
          />
        );
      }}
    />
  );
}
